import { trackingClient } from '../clients'
import { TrackingEvent } from '../transports/tracking-transport'

// ─── Generic debounced batch queue for tracking events ──────────────────────
// Collects tracking events and flushes them in a single API call
// after a short debounce window to reduce API requests.

const FLUSH_DEBOUNCE_MS = 500

type TrackingMethod = 'trackEventsV2' | 'trackRegionLandingPageListings'

interface QueueConfig {
    method: TrackingMethod
    debounceMs?: number
}

class TrackingQueue {
    private queue: any[] = []
    private timer: ReturnType<typeof setTimeout> | null = null
    private method: TrackingMethod
    private debounceMs: number

    constructor(config: QueueConfig) {
        this.method = config.method
        this.debounceMs = config.debounceMs ?? FLUSH_DEBOUNCE_MS
    }

    private flush = () => {
        if (this.queue.length === 0) {
            return
        }
        const batch = this.queue.splice(0) // drain atomically

        if (this.method === 'trackEventsV2') {
            trackingClient.trackEventsV2({ trackingEvents: batch }).then()
        } else if (this.method === 'trackRegionLandingPageListings') {
            trackingClient.trackRegionLandingPageListings(batch).then()
        }
    }

    public enqueue(events: any | any[]) {
        const eventsArray = Array.isArray(events) ? events : [events]
        this.queue.push(...eventsArray)
        if (this.timer) {
            clearTimeout(this.timer)
        }
        this.timer = setTimeout(this.flush, this.debounceMs)
    }
}

// Queue for SRL view events (uses trackEventsV2)
const srlQueue = new TrackingQueue({ method: 'trackEventsV2' })

// Queue for region landing page tracking (uses trackRegionLandingPageListings)
const regionLandingPageQueue = new TrackingQueue({ method: 'trackRegionLandingPageListings' })

export const enqueueViewEvents = (events: TrackingEvent[]) => {
    srlQueue.enqueue(events)
}

export const enqueueRegionLandingPageEvents = (events: any | any[]) => {
    regionLandingPageQueue.enqueue(events)
}
